import { useCallback, useEffect, useMemo, useState } from "react";

import { useSearchParams } from "@remix-run/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";

import { useApiClient } from "~/hooks/use-api-client";
import { useAnonymous } from "~/hooks/use-anonymous";
import { useSessionResearchRelease } from "~/hooks/use-session-research-release";
import { trackEvent } from "~/lib/analytics";
import {
    EmbeddedSurveyPrompt,
    useAnswerSurveyDeliveryPrompt,
    useDismissSurveyDeliveryPrompt,
    useSurveyDeliveryPrompt,
} from "~/features/surveys";
import {
    normalizeFeedbackFormKey,
    toFeedbackSurfaceKey,
} from "~/features/surveys/feedback-surfaces";

// AIDEV-NOTE: Phase 1 - Import paths updated to correct ui location
import { Alert } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
} from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

const useSubmitFeedback = () => {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({
            sessionId,
            partId,
            feedbackText,
            score,
            feedbackId,
            metadata,
            requestFollowupSurvey,
        }: {
            sessionId?: string;
            partId?: string;
            feedbackText: string;
            score: -1 | 0 | 1;
            feedbackId?: string;
            metadata?: Record<string, unknown>;
            requestFollowupSurvey?: boolean;
        }) => {
            const response = await apiClient.post("/feedback", {
                ...(sessionId && { sessionId }),
                ...(partId && { partId }),
                content: feedbackText,
                score,
                ...(feedbackId && { feedbackId }),
                ...(metadata && { metadata }),
                ...(requestFollowupSurvey && { requestFollowupSurvey: true }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || "Failed to submit feedback");
            }

            return response.json();
        },
        onSuccess: () => {
            toast.success("Thanks for the feedback!");
        },
        onError: (error) => {
            toast.error(error.message || "Failed to submit feedback");
        },
    });
};

export function FeedbackSection({
    sessionId,
    text,
    partId,
    feature,
    metadata,
    compact = false,
    feedbackFormKey,
    variant = "default",
    iconOnly = false,
}: {
    sessionId: string;
    text?: string;
    partId?: string;
    feature?: string;
    metadata?: Record<string, unknown>;
    compact?: boolean;
    feedbackFormKey?: string;
    // AIDEV-NOTE: "subtle" is the Session Notes redesign (mockup 2) feedback —
    // small bordered thumb buttons + an italic forest prompt, calmer than the
    // default terra/green circle buttons. `iconOnly` drops the prompt entirely
    // (used in tight headers like the My Parts page).
    variant?: "default" | "subtle";
    iconOnly?: boolean;
}) {
    // AIDEV-NOTE: New feedback surfaces should also register a survey delivery service/surface key.
    const { isAnonymous } = useAnonymous();
    const [searchParams] = useSearchParams();
    const isPublicSession = !!searchParams.get("sessionToken");
    const [feedbackText, setFeedbackText] = useState("");
    const submitFeedback = useSubmitFeedback();
    const [currentFeedbackId, setCurrentFeedbackId] = useState<string>();
    const {
        sessionHasResearchReleaseEnabled,
        effectiveResearchReleaseEnabled,
        toggleResearchRelease,
        isPending: isResearchReleasePending,
    } = useSessionResearchRelease({ sessionId });

    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    // AIDEV-NOTE: Drives post-submit UX: positive replaces the thanks alert with the
    // follow-up survey; negative keeps the dialog open so the survey + thanks live
    // inside the modal and auto-closes 2s after the survey is answered.
    const [submittedSentiment, setSubmittedSentiment] = useState<
        "positive" | "negative" | null
    >(null);
    const [followupCompleted, setFollowupCompleted] = useState(false);
    const resolvedFeedbackFormKey = normalizeFeedbackFormKey({
        explicitKey: feedbackFormKey,
        metadata,
        feature,
        partId,
    });
    const feedbackSurveySurfaceKey = toFeedbackSurfaceKey(
        resolvedFeedbackFormKey,
    );
    // AIDEV-NOTE: Always persist a `type` so admin feedback classification never
    // falls through to the default "Session" bucket. Surfaces like session summary,
    // parts list/graph, and part detail pass only `feature`/`text` (no metadata.type),
    // which previously wrote null/null rows. Prefer an explicit caller type, else
    // derive from the resolved surface key. classifyFeature() maps these surface keys.
    const metadataWithFeedbackKey = useMemo(
        () => ({
            ...metadata,
            feedbackFormKey: resolvedFeedbackFormKey,
            type:
                (typeof metadata?.type === "string" && metadata.type) ||
                resolvedFeedbackFormKey,
        }),
        [metadata, resolvedFeedbackFormKey],
    );
    const followupContext = useMemo(
        () => ({
            feedbackFormKey: resolvedFeedbackFormKey,
            ...(sessionId && { sessionId }),
            ...(partId && { partId }),
        }),
        [resolvedFeedbackFormKey, sessionId, partId],
    );
    const { data: followupPrompt, isLoading: isFollowupLoading } =
        useSurveyDeliveryPrompt({
            surfaceKey: feedbackSurveySurfaceKey,
            enabled: !isAnonymous && !isPublicSession && isSubmitted,
            context: followupContext,
        });
    const answerFollowup = useAnswerSurveyDeliveryPrompt(
        feedbackSurveySurfaceKey,
    );
    const dismissFollowup = useDismissSurveyDeliveryPrompt(
        feedbackSurveySurfaceKey,
    );

    const resetPostSubmitState = useCallback(() => {
        setIsSubmitted(false);
        setSubmittedSentiment(null);
        setFollowupCompleted(false);
    }, []);

    // AIDEV-NOTE: Negative path lives inside the dialog. Auto-close 2s after either
    // the follow-up survey is answered, or we've confirmed no follow-up exists.
    useEffect(() => {
        if (submittedSentiment !== "negative" || !isSubmitted || !isOpen) {
            return;
        }
        const noSurveyAvailable = !isFollowupLoading && !followupPrompt;
        if (!noSurveyAvailable && !followupCompleted) {
            return;
        }
        const timer = setTimeout(() => {
            setIsOpen(false);
            resetPostSubmitState();
        }, 2000);
        return () => clearTimeout(timer);
    }, [
        submittedSentiment,
        isSubmitted,
        isOpen,
        isFollowupLoading,
        followupPrompt,
        followupCompleted,
        resetPostSubmitState,
    ]);

    // AIDEV-NOTE: Hide feedback component for anonymous users OR public session viewers
    if (isAnonymous || isPublicSession) {
        return null;
    }

    const handleModalOpen = async (open: boolean) => {
        if (open) {
            // Create initial feedback when opening modal
            trackEvent("rated content", {
                rating: "negative",
                feature: partId ? "part summary" : "session summary",
                session: sessionId,
                ownership: "owner",
            });

            const result = await submitFeedback.mutateAsync({
                sessionId,
                partId,
                feedbackText: "",
                score: -1,
                metadata: metadataWithFeedbackKey,
            });

            if (result?.createdFeedbackId) {
                setCurrentFeedbackId(result.createdFeedbackId);
            }
        } else if (submittedSentiment === "negative") {
            // AIDEV-NOTE: User dismissed the post-submit dialog manually; clear state
            // so the thumbs UI reappears (no lingering thanks alert).
            resetPostSubmitState();
        }
        setIsOpen(open);
    };

    // AIDEV-NOTE: Seed the survey delivery cache from the feedback submission
    // response so the followup renders without a second round-trip. Key must
    // exactly match useSurveyDeliveryPrompt's queryKey.
    const seedFollowupCache = useCallback(
        (result: unknown) => {
            const prompt = (result as { nextSurveyPrompt?: unknown })
                ?.nextSurveyPrompt;
            if (prompt === undefined) return;
            queryClient.setQueryData(
                [
                    "surveys",
                    "delivery",
                    feedbackSurveySurfaceKey,
                    followupContext,
                ],
                prompt,
            );
        },
        [queryClient, feedbackSurveySurfaceKey, followupContext],
    );

    const submit = useCallback(async () => {
        await submitFeedback.mutateAsync(
            {
                sessionId,
                partId,
                feedbackText,
                score: -1,
                feedbackId: currentFeedbackId,
                metadata: metadataWithFeedbackKey,
                requestFollowupSurvey: true,
            },
            {
                onSuccess: (result) => {
                    trackEvent("sent supervision feedback", {
                        feature:
                            feature || partId
                                ? "part summary"
                                : "session summary",
                        length: feedbackText.length,
                        session: sessionId,
                        ownership: "owner",
                    });
                    setFeedbackText("");
                    seedFollowupCache(result);
                    // AIDEV-NOTE: Keep dialog open so the follow-up survey + thanks
                    // render inside it; auto-close handled by effect above.
                    setSubmittedSentiment("negative");
                    setIsSubmitted(true);
                    setCurrentFeedbackId(undefined);
                },
            },
        );
    }, [
        submitFeedback,
        sessionId,
        partId,
        feedbackText,
        currentFeedbackId,
        feature,
        metadataWithFeedbackKey,
        seedFollowupCache,
    ]);

    const submitPositive = useCallback(async () => {
        trackEvent("rated content", {
            rating: "positive",
            feature: partId ? "part summary" : "session summary",
            session: sessionId,
            ownership: "owner",
        });

        await submitFeedback.mutateAsync(
            {
                sessionId,
                partId,
                feedbackText: "",
                score: 1,
                metadata: metadataWithFeedbackKey,
                requestFollowupSurvey: true,
            },
            {
                onSuccess: (result) => {
                    seedFollowupCache(result);
                    setSubmittedSentiment("positive");
                    setIsSubmitted(true);
                },
            },
        );
    }, [
        partId,
        sessionId,
        submitFeedback,
        metadataWithFeedbackKey,
        seedFollowupCache,
    ]);

    if (isSubmitted && submittedSentiment === "positive") {
        // AIDEV-NOTE: When a follow-up exists, replace the thanks alert with the
        // survey directly. After the user answers or dismisses, fall through to the
        // thanks alert below.
        if (followupPrompt && !followupCompleted) {
            return (
                <div className="space-y-3 print:hidden">
                    <EmbeddedSurveyPrompt
                        prompt={followupPrompt}
                        title="One more question"
                        onAnswer={(answer, question, deliveryId) =>
                            answerFollowup.mutate({
                                prompt: {
                                    ...followupPrompt,
                                    question,
                                    deliveryId,
                                },
                                answer,
                            })
                        }
                        onComplete={() => setFollowupCompleted(true)}
                        onDismiss={() =>
                            dismissFollowup.mutate({ prompt: followupPrompt })
                        }
                        isSubmitting={
                            answerFollowup.isPending ||
                            dismissFollowup.isPending
                        }
                    />
                </div>
            );
        }
        // AIDEV-NOTE: Suppress thanks while followup is still loading so the
        // survey doesn't get preceded by a flash of "Thanks for sharing!".
        if (isFollowupLoading) {
            return null;
        }
        return (
            <div className="space-y-3 print:hidden">
                <Alert
                    className={`flex flex-col items-stretch gap-3 p-3 sm:flex-row sm:items-center sm:justify-between ${
                        compact ? "w-auto" : "mx-auto"
                    }`}
                >
                    <div
                        className={compact ? "text-sm" : "text-sm sm:text-base"}
                    >
                        Thanks for sharing!
                    </div>
                    <Button
                        variant="ghostIcon"
                        size={compact ? "sm" : "default"}
                        onClick={resetPostSubmitState}
                        className="self-end sm:self-auto"
                    >
                        Close
                    </Button>
                </Alert>
            </div>
        );
    }

    const isSubtle = variant === "subtle";

    return (
        <div
            className={cn(
                "flex items-center justify-end print:hidden",
                isSubtle ? "gap-2.5" : "gap-2",
            )}
        >
            {!iconOnly && (
                <div
                    className={cn(
                        isSubtle
                            ? "text-sm italic text-brand-forest-600"
                            : "text-sm md:text-lg font-medium text-brand-forest-800",
                    )}
                >
                    {text ?? "Does this feel right?"}
                </div>
            )}
            <div
                className={cn(
                    "flex items-center",
                    isSubtle ? "gap-2" : "gap-4",
                )}
            >
                <Dialog
                    open={isOpen}
                    onOpenChange={handleModalOpen}
                    analyticsId="Feedback"
                >
                    <DialogTrigger asChild>
                        {isSubtle ? (
                            <button
                                type="button"
                                aria-label="This could be better"
                                className="inline-flex size-8 items-center justify-center rounded-[10px] border border-brand-forest-200 bg-white text-brand-forest-500 transition-colors hover:bg-brand-forest-50 hover:text-brand-forest-700"
                            >
                                <ThumbsDown className="size-[15px]" />
                            </button>
                        ) : (
                            <Button
                                variant="circle"
                                size="icon"
                                className="text-brand-terra-600 hover:bg-brand-cream-100 hover:text-brand-forest-700"
                            >
                                <ThumbsDown
                                    className="size-5"
                                    strokeWidth={3}
                                />
                            </Button>
                        )}
                    </DialogTrigger>
                    <DialogPortal>
                        <DialogOverlay />
                        <DialogContent>
                            {isSubmitted &&
                            submittedSentiment === "negative" ? (
                                // AIDEV-NOTE: Post-submit state inside the modal.
                                // Title is constant; survey (if any) renders below.
                                // Modal's own X handles dismissal — survey's X is
                                // hidden by omitting onDismiss to avoid double X.
                                <>
                                    <DialogTitle>
                                        Thanks for the feedback
                                    </DialogTitle>
                                    {followupPrompt && !followupCompleted && (
                                        <EmbeddedSurveyPrompt
                                            prompt={followupPrompt}
                                            title="One more question"
                                            onAnswer={(
                                                answer,
                                                question,
                                                deliveryId,
                                            ) =>
                                                answerFollowup.mutate({
                                                    prompt: {
                                                        ...followupPrompt,
                                                        question,
                                                        deliveryId,
                                                    },
                                                    answer,
                                                })
                                            }
                                            onComplete={() =>
                                                setFollowupCompleted(true)
                                            }
                                            isSubmitting={
                                                answerFollowup.isPending ||
                                                dismissFollowup.isPending
                                            }
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    <DialogTitle>
                                        What could have been better?
                                    </DialogTitle>
                                    <DialogDescription>
                                        <Textarea
                                            value={feedbackText}
                                            onChange={(e) =>
                                                setFeedbackText(e.target.value)
                                            }
                                            placeholder="Tell us more about what you noticed here... (optional)"
                                            className="mt-4"
                                        />
                                    </DialogDescription>
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            onClick={submit}
                                            isLoading={submitFeedback.isPending}
                                            disabled={submitFeedback.isPending}
                                        >
                                            {submitFeedback.isPending
                                                ? "Submitting..."
                                                : "Submit"}
                                        </Button>
                                    </div>
                                    {/* Only show research release option if there's a sessionId */}
                                    {sessionId && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="research-release"
                                                className="border-brand-forest-800"
                                                checked={
                                                    effectiveResearchReleaseEnabled ??
                                                    false
                                                }
                                                onCheckedChange={(checked) => {
                                                    // AIDEV-NOTE: Only toggle if value actually changes.
                                                    // This prevents Radix Checkbox from triggering unwanted updates
                                                    // during initialization when sessionHasResearchReleaseEnabled
                                                    // changes from undefined to a value.
                                                    const newValue =
                                                        checked as boolean;
                                                    if (
                                                        newValue !==
                                                        sessionHasResearchReleaseEnabled
                                                    ) {
                                                        toggleResearchRelease(
                                                            newValue,
                                                        );
                                                    }
                                                }}
                                                disabled={
                                                    isResearchReleasePending
                                                }
                                            />
                                            <label
                                                className="text-md text-brand-forest-800"
                                                htmlFor="research-release"
                                            >
                                                Share this session with the IFS
                                                Companion team
                                            </label>
                                        </div>
                                    )}
                                </>
                            )}
                        </DialogContent>
                    </DialogPortal>
                </Dialog>
                {isSubtle ? (
                    <button
                        type="button"
                        aria-label="This feels right"
                        onClick={submitPositive}
                        className="inline-flex size-8 items-center justify-center rounded-[10px] border border-brand-forest-300 bg-white text-brand-forest-600 transition-colors hover:bg-brand-forest-50 hover:text-brand-forest-700"
                    >
                        <ThumbsUp className="size-[15px]" />
                    </button>
                ) : (
                    <Button
                        variant="circle"
                        size="icon"
                        className="text-green-600 hover:bg-green-50 hover:text-green-700"
                        onClick={submitPositive}
                    >
                        <ThumbsUp className="size-5" strokeWidth={3} />
                    </Button>
                )}
            </div>
        </div>
    );
}
